<template>
  <v-container fluid class="pa-4">
    <v-card class="legajo-virtual-card elevation-4" rounded="lg">
      <!-- Header with gradient -->
      <v-toolbar color="orange darken-1" dark flat prominent>
        <v-icon large class="mr-3">folder_open</v-icon>
        <v-toolbar-title class="text-h5 font-weight-medium">
          Legajo Virtual V2
        </v-toolbar-title>

        <v-spacer></v-spacer>
        <v-chip
          v-if="archivosPersonaFiltrados.length > 0"
          color="white"
          text-color="orange darken-2"
          class="font-weight-bold"
        >
          <v-icon left small>attach_file</v-icon>
          {{ archivosPersonaFiltrados.length }}
          {{ archivosPersonaFiltrados.length === 1 ? "archivo" : "archivos" }}
        </v-chip>
      </v-toolbar>

      <!-- Selector de empresa -->
      <v-card-text v-if="showSelectorEmpresa" class="pb-0">
        <v-row>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="empresaId"
              :items="empresas"
              autocomplete="disabled"
              label="Filtrar por empresa"
              item-text="nombre"
              item-value="id"
              outlined
              dense
              hide-details
              prepend-inner-icon="business"
              clearable
            ></v-autocomplete>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Barra de búsqueda y filtros -->
      <v-card-text class="pb-0">
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="busquedaArchivo"
              placeholder="Buscar archivo por nombre..."
              prepend-inner-icon="search"
              outlined
              dense
              hide-details
              clearable
              class="mb-2"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end">
            <v-btn-toggle
              v-model="vistaActual"
              mandatory
              dense
              color="orange darken-1"
              class="mb-2"
            >
              <v-btn small value="lista">
                <v-icon small>view_list</v-icon>
              </v-btn>
              <v-btn small value="tabla">
                <v-icon small>view_module</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Filtros con chips -->
      <v-card-text class="pt-2">
        <div class="d-flex align-center flex-wrap">
          <span class="text-subtitle-2 grey--text text--darken-2 mr-3"
            >Tipo:</span
          >
          <v-chip-group
            v-model="filtroTipoDocumentacion"
            active-class=""
            column
            class="filter-chip-group"
          >
            <v-chip filter outlined small>
              <v-icon left x-small>description</v-icon>
              Cupo
            </v-chip>
            <v-chip filter outlined small>
              <v-icon left x-small>how_to_reg</v-icon>
              Legajo
            </v-chip>
            <v-chip filter outlined small>
              <v-icon left x-small>payment</v-icon>
              Comprobantes
            </v-chip>
            <v-chip filter outlined small>
              <v-icon left x-small>receipt</v-icon>
              Factura
            </v-chip>
            <v-chip filter outlined small>
              <v-icon left x-small>mic</v-icon>
              Grabaciones
            </v-chip>
            <v-chip
              filter
              outlined
              small
              v-show="checkCanAccess('ap-acceso-formularios-internos')"
            >
              <v-icon left x-small>lock</v-icon>
              Interno
            </v-chip>
            <v-chip filter outlined small>
              <v-icon left x-small>file_copy</v-icon>
              Otros
            </v-chip>
          </v-chip-group>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Tabs para separar visualización y carga -->
      <v-tabs v-model="tabActiva" color="orange darken-1" grow>
        <v-tab>
          <v-icon left>folder</v-icon>
          Ver Archivos
          <v-chip
            x-small
            class="ml-2"
            v-if="archivosPersonaFiltrados.length > 0"
          >
            {{ archivosPersonaFiltrados.length }}
          </v-chip>
        </v-tab>
        <v-tab>
          <v-icon left>cloud_upload</v-icon>
          Subir Archivos
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tabActiva">
        <!-- TAB 1: Ver Archivos -->
        <v-tab-item>
          <!-- Vista Lista -->
          <v-list v-if="vistaActual === 'lista'" two-line class="py-0"
            >`

            <v-list-item
              v-for="item in archivosPersonaFiltradosBusqueda"
              :key="item.fileName"
              class="file-list-item"
            >
              <v-list-item-avatar color="orange lighten-4" size="48">
                <v-icon :class="[item.iconClass]" color="orange darken-2">
                  {{ item.icon }}
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ item.fileName }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  <v-chip x-small outlined class="mr-2">{{
                    obtenerTipoTexto(item.tipo)
                  }}</v-chip>
                  <v-icon x-small class="mr-1">event</v-icon>
                  {{ item.fechaCreacion }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <!-- Menú de acciones más compacto -->
              <v-list-item-action>
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      @click="verArchivo(item)"
                      v-if="isImageOrPdf(item.extension)"
                    >
                      <v-list-item-icon>
                        <v-icon color="indigo">visibility</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Ver archivo</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="downloadFile(item)">
                      <v-list-item-icon>
                        <v-icon color="green">cloud_download</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Descargar</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="changeType(item)"
                      v-if="checkCanAccess('ap-delete-file-legajo')"
                    >
                      <v-list-item-icon>
                        <v-icon color="orange">edit</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Recategorizar</v-list-item-title>
                    </v-list-item>
                    <v-divider
                      v-if="checkCanAccess('ap-delete-file-legajo')"
                    ></v-divider>
                    <v-list-item
                      @click="deleteArchivo(item)"
                      v-if="checkCanAccess('ap-delete-file-legajo')"
                    >
                      <v-list-item-icon>
                        <v-icon color="red">delete</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Eliminar</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>

            <!-- Empty state -->
            <v-list-item
              v-if="archivosPersonaFiltradosBusqueda.length === 0"
              class="text-center py-12"
            >
              <v-list-item-content>
                <v-icon size="64" color="grey lighten-2" class="mb-4">
                  {{ busquedaArchivo ? "search_off" : "folder_open" }}
                </v-icon>
                <v-list-item-title class="grey--text text--darken-1">
                  {{
                    busquedaArchivo
                      ? "No se encontraron archivos con ese nombre"
                      : "No se encontraron archivos"
                  }}
                </v-list-item-title>
                <v-list-item-subtitle class="grey--text">
                  {{
                    filtroTipoDocumentacion !== null && !busquedaArchivo
                      ? "Intenta cambiar el filtro seleccionado"
                      : "Aún no hay archivos cargados"
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Vista Tabla -->
          <v-card flat v-else>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Archivo</th>
                    <th class="text-left">Tipo</th>
                    <th class="text-left">Fecha</th>
                    <th class="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in archivosPersonaFiltradosBusqueda"
                    :key="item.fileName"
                    class="file-table-row"
                  >
                    <td>
                      <div class="d-flex align-center">
                        <v-icon
                          :class="[item.iconClass]"
                          color="orange darken-2"
                          class="mr-2"
                        >
                          {{ item.icon }}
                        </v-icon>
                        <span class="font-weight-medium">{{
                          item.fileName
                        }}</span>
                      </div>
                    </td>
                    <td>
                      <v-chip x-small outlined>{{
                        obtenerTipoTexto(item.tipo)
                      }}</v-chip>
                    </td>
                    <td>{{ item.fechaCreacion }}</td>
                    <td class="text-center">
                      <v-tooltip bottom v-if="isImageOrPdf(item.extension)">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="verArchivo(item)"
                            icon
                            small
                            color="indigo"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>visibility</v-icon>
                          </v-btn>
                        </template>
                        <span>Ver</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="downloadFile(item)"
                            icon
                            small
                            color="green"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>cloud_download</v-icon>
                          </v-btn>
                        </template>
                        <span>Descargar</span>
                      </v-tooltip>
                      <v-tooltip
                        bottom
                        v-if="checkCanAccess('ap-delete-file-legajo')"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="changeType(item)"
                            icon
                            small
                            color="orange"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>edit</v-icon>
                          </v-btn>
                        </template>
                        <span>Recategorizar</span>
                      </v-tooltip>
                      <v-tooltip
                        bottom
                        v-if="checkCanAccess('ap-delete-file-legajo')"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            @click="deleteArchivo(item)"
                            icon
                            small
                            color="red"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Eliminar</span>
                      </v-tooltip>
                    </td>
                  </tr>
                  <tr v-if="archivosPersonaFiltradosBusqueda.length === 0">
                    <td colspan="4" class="text-center py-8">
                      <v-icon size="48" color="grey lighten-2" class="mb-2">
                        {{ busquedaArchivo ? "search_off" : "folder_open" }}
                      </v-icon>
                      <div class="grey--text">
                        {{
                          busquedaArchivo
                            ? "No se encontraron archivos con ese nombre"
                            : "No hay archivos para mostrar"
                        }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Dialog de Recategorización (se muestra independiente de la vista) -->
          <v-dialog
            v-model="mostrarDialogRecategorizacion"
            max-width="400"
            persistent
          >
            <v-card>
              <v-card-title class="orange darken-1 white--text">
                <v-icon left color="white">edit</v-icon>
                Recategorizar Archivo
              </v-card-title>
              <v-card-text class="pt-4">
                <div class="mb-3 font-weight-medium">
                  {{ selectedItem ? selectedItem.fileName : "" }}
                </div>
                <v-radio-group v-model="tipoDocumentacion" dense>
                  <v-radio value="documentacion" class="mb-1">
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2"
                          >description</v-icon
                        >
                        <span class="text-body-2">Cupo</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="legajoCredito" class="mb-1">
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2"
                          >how_to_reg</v-icon
                        >
                        <span class="text-body-2">Legajo Firmado</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="grabaciones" class="mb-1">
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2">mic</v-icon>
                        <span class="text-body-2">Grabaciones</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="comprobantePago" class="mb-1">
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2"
                          >payment</v-icon
                        >
                        <span class="text-body-2">Comprobantes Pago</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="factura" class="mb-1">
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2"
                          >receipt</v-icon
                        >
                        <span class="text-body-2">Factura</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio
                    value="interno"
                    class="mb-1"
                    v-show="checkCanAccess('ap-acceso-formularios-internos')"
                  >
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2"
                          >layers</v-icon
                        >
                        <span class="text-body-2">Interno</span>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="sinEspecificar" class="mb-1">
                    <template v-slot:label>
                      <div class="d-flex align-center">
                        <v-icon color="indigo" small class="mr-2"
                          >file_copy</v-icon
                        >
                        <span class="text-body-2">Sin Especificar</span>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="grey" @click="cerrarRecategorizacion">
                  Cancelar
                </v-btn>
                <v-btn color="success" depressed @click="recategorizar">
                  <v-icon left small>check</v-icon>
                  Confirmar
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tab-item>

        <!-- TAB 2: Subir Archivos -->
        <v-tab-item>
          <!-- Sección de carga de archivos -->
          <v-card-text class="upload-section">
            <v-row align="center" class="mb-3">
              <v-col cols="12" md="4">
                <v-switch
                  v-model="agregarArchivos"
                  label="Agregar archivos"
                  color="orange darken-1"
                  inset
                  hide-details
                ></v-switch>
              </v-col>
              <v-col
                cols="12"
                md="8"
                v-if="showSelectorEmpresa && agregarArchivos"
              >
                <v-autocomplete
                  v-model="empresaSubidaId"
                  :items="empresas.filter((e) => e.id != 0)"
                  autocomplete="disabled"
                  label="Cargar para la empresa"
                  item-text="nombre"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="business"
                ></v-autocomplete>
              </v-col>
            </v-row>

            <v-expand-transition>
              <div v-if="agregarArchivos">
                <v-file-input
                  v-model="archivosASubir"
                  placeholder="Seleccione o arrastre los archivos a subir"
                  label="Archivos a subir"
                  chips
                  multiple
                  counter
                  show-size
                  prepend-icon="attach_file"
                  outlined
                  dense
                  class="mb-4"
                ></v-file-input>

                <!-- Checkbox para completar documentación -->
                <v-card
                  v-if="showCompletarDocumentacion"
                  outlined
                  class="mb-4 pa-3"
                >
                  <v-checkbox
                    v-model="completarDocumentacion"
                    hide-details
                    class="mt-0"
                  >
                    <template v-slot:label>
                      <v-chip
                        color="warning"
                        text-color="black"
                        small
                        class="font-weight-medium"
                      >
                        <v-icon left small>update</v-icon>
                        Actualizar estado del crédito
                      </v-chip>
                    </template>
                  </v-checkbox>
                  <v-expand-transition>
                    <v-textarea
                      v-if="completarDocumentacion"
                      class="mt-3"
                      outlined
                      dense
                      rows="3"
                      label="Comentarios"
                      v-model="comentarios"
                      placeholder="Ingrese comentarios sobre la actualización..."
                    ></v-textarea>
                  </v-expand-transition>
                </v-card>

                <!-- Tipo de documentación -->
                <v-card outlined class="pa-4">
                  <v-card-subtitle
                    class="pa-0 pb-3 text-subtitle-2 font-weight-bold grey--text text--darken-2"
                  >
                    Tipo de documentación a adjuntar
                  </v-card-subtitle>
                  <v-radio-group v-model="tipoDocumentacion" class="mt-0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="documentacion"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3"
                                >description</v-icon
                              >
                              <div>
                                <div class="font-weight-medium">Cupo</div>
                                <div class="text-caption grey--text">
                                  Recibos, movimientos, copias, etc.
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar Recibos, Movimientos, Copia de
                        Documento, etc.</span
                      >
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="legajoCredito"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3"
                                >how_to_reg</v-icon
                              >
                              <div>
                                <div class="font-weight-medium">
                                  Legajo Firmado
                                </div>
                                <div class="text-caption grey--text">
                                  Solicitud o legajo firmado
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar, exclusivamente, la Solicitud o
                        Legajo Firmados</span
                      >
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="grabaciones"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3">mic</v-icon>
                              <div>
                                <div class="font-weight-medium">
                                  Grabaciones
                                </div>
                                <div class="text-caption grey--text">
                                  Archivos de audio
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar, exclusivamente, archivos de
                        audio</span
                      >
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="comprobantePago"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3"
                                >payment</v-icon
                              >
                              <div>
                                <div class="font-weight-medium">
                                  Comprobantes de Pago
                                </div>
                                <div class="text-caption grey--text">
                                  Comprobantes de pago
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar, exclusivamente, archivos que
                        contengan Comprobantes de Pago</span
                      >
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="factura"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3"
                                >receipt</v-icon
                              >
                              <div>
                                <div class="font-weight-medium">Factura</div>
                                <div class="text-caption grey--text">
                                  Facturas y notas de crédito
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar, exclusivamente, facturas y
                        documentos relacionados</span
                      >
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="interno"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3"
                                >layers</v-icon
                              >
                              <div>
                                <div class="font-weight-medium">Interno</div>
                                <div class="text-caption grey--text">
                                  Archivos internos
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar, exclusivamente, para archivos
                        internos</span
                      >
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-radio
                          value="sinEspecificar"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-2"
                        >
                          <template v-slot:label>
                            <div class="d-flex align-center">
                              <v-icon color="indigo" class="mr-3"
                                >file_copy</v-icon
                              >
                              <div>
                                <div class="font-weight-medium">
                                  Sin Especificar
                                </div>
                                <div class="text-caption grey--text">
                                  Otros documentos
                                </div>
                              </div>
                            </div>
                          </template>
                        </v-radio>
                      </template>
                      <span
                        >Utilizar al adjuntar, exclusivamente, archivos que no
                        comprendan las otras categorías</span
                      >
                    </v-tooltip>
                  </v-radio-group>
                </v-card>

                <v-expand-transition>
                  <v-card
                    v-if="tipoDocumentacion === 'factura'"
                    outlined
                    class="mt-4 pa-4 credit-association-card"
                  >
                    <div class="d-flex align-center mb-3">
                      <v-icon color="orange darken-1" class="mr-2">
                        account_balance
                      </v-icon>
                      <div class="text-subtitle-2 font-weight-bold">
                        Asociar con Crédito
                      </div>
                    </div>
                    <v-row>
                      <v-col cols="12" md="8">
                        <v-autocomplete
                          v-model="creditoId"
                          :items="creditosPersonaOptions"
                          :loading="loadingCreditos"
                          :disabled="
                            loadingCreditos ||
                            creditosPersonaOptions.length === 0
                          "
                          label="Seleccioná el crédito"
                          item-text="_label"
                          item-value="_value"
                          outlined
                          dense
                          hide-details
                          prepend-inner-icon="credit_card"
                          clearable
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4" class="d-flex align-center">
                        <v-btn
                          color="orange darken-1"
                          text
                          small
                          @click="loadCreditosPersona"
                          :loading="loadingCreditos"
                        >
                          <v-icon left small>refresh</v-icon>
                          Actualizar
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-alert
                      v-if="
                        !loadingCreditos && creditosPersonaOptions.length === 0
                      "
                      type="info"
                      border="left"
                      colored-border
                      color="orange lighten-4"
                      class="mb-0"
                      dense
                    >
                      No encontramos créditos asociados a la persona.
                    </v-alert>
                  </v-card>
                </v-expand-transition>
              </div>
            </v-expand-transition>
          </v-card-text>

          <!-- Actions en la pestaña de subida -->
          <v-card-actions class="px-6 py-4">
            <v-btn
              v-if="agregarArchivos"
              color="orange darken-1"
              dark
              large
              depressed
              @click="uploadFiles"
              :disabled="!archivosASubir || archivosASubir.length === 0"
            >
              <v-icon left>cloud_upload</v-icon>
              Subir archivos
            </v-btn>
          </v-card-actions>
        </v-tab-item>
      </v-tabs-items>

      <!-- Botón cerrar global (fuera de tabs) -->
      <v-divider></v-divider>
      <v-card-actions class="px-6 py-3 grey lighten-4">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" outlined @click="closeDialog">
          <v-icon left>close</v-icon>
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialogImagen" max-width="900">
      <v-layout align-center justify-center>
        <v-img
          v-bind:src="fileImagenUrl"
          contain
          :aspect-ratio="16 / 9"
          class="grey lighten-2"
        >
          <template v-slot:placeholder>
            <v-layout fill-height align-center justify-center ma-0>
              <v-progress-circular
                indeterminate
                color="grey lighten-5"
              ></v-progress-circular>
            </v-layout>
          </template>
        </v-img>
      </v-layout>
    </v-dialog>
  </v-container>
</template>

<script>
import { http } from "@/axios";
import legajoVirtualService from "@/services/legajoVirtual.service";

import {
  //mapState,
  mapGetters,
  mapMutations,
  //mapActions
} from "vuex";

export default {
  data() {
    return {
      active: null,
      loading: false,
      editMode: true,
      showAgregar: true,
      agregarArchivos: false,
      tipoDocumentacion: "",
      archivosPersona: [],
      archivosASubir: [],
      dialogImagen: false,
      fileImagenUrl: "",
      empresas: [],
      empresaId: -1,
      empresaSubidaId: -1,
      showSelectorEmpresa: false,
      completarDocumentacion: false,
      comentarios: "",
      filtroTipoDocumentacion: null,
      selectedItem: null,
      personaInternaId: this.personaId || null,
      creditosPersona: [],
      loadingCreditos: false,
      creditoId: this.solicitudId || null,
      // Nuevas propiedades para mejoras de usabilidad
      busquedaArchivo: "",
      vistaActual: "tabla", // 'lista' o 'tabla'
      tabActiva: 0, // 0 = Ver Archivos, 1 = Subir Archivos
      mostrarDialogRecategorizacion: false,
    };
  },
  async created() {
    await this.loadEmpresas();
    //if (this.documento || this.personaId) this.load();
    if (this.documento) this.load();
  },
  props: {
    documento: { type: String },
    personaId: { type: String, default: null },
    solicitudId: { type: String },
    showCompletarDocumentacion: { type: Boolean, default: false },
  },
  watch: {
    documento: function () {
      if (this.documento) {
        this.clear();
        this.personaInternaId = this.personaId || null;
        this.creditosPersona = [];
        this.creditoId = this.solicitudId || null;
        this.load();
        if (this.tipoDocumentacion === "factura") {
          this.prepareCreditosAsociacion();
        }
        //this.showAgregar = false;
      }
    },
    personaId: function (nuevoValor) {
      this.personaInternaId = nuevoValor || null;
      this.creditosPersona = [];
      this.creditoId = this.solicitudId || null;
      if (nuevoValor && this.tipoDocumentacion === "factura") {
        this.prepareCreditosAsociacion();
      }
    },
    tipoDocumentacion(newValue) {
      if (newValue === "factura") {
        this.prepareCreditosAsociacion();
      } else {
        this.creditoId = null;
      }
    },
    agregarArchivos(val) {
      if (val && this.tipoDocumentacion === "factura") {
        this.prepareCreditosAsociacion();
      }
    },
    esDocumentacion: function () {
      this.esLegajoFirmado = !this.esDocumentacion;
    },
  },
  computed: {
    ...mapGetters([
      "UiCustomUserSettings",
      "user",
      "DebugModeOn",
      "checkCanAccess",
    ]),
    creditosPersonaOptions() {
      if (!this.creditosPersona || this.creditosPersona.length === 0) {
        return [];
      }

      return this.creditosPersona.map((credito) => ({
        ...credito,
        _value: credito.creditoId || credito.id,
        _label: this.formatearCredito(credito),
      }));
    },
    archivosPersonaFiltrados() {
      if (this.filtroTipoDocumentacion == null)
        if (this.checkCanAccess("ap-acceso-formularios-internos"))
          return this.archivosPersona;
        else return this.archivosPersona.filter((i) => i.tipo != "Interno");

      if (this.filtroTipoDocumentacion == 0)
        return this.archivosPersona.filter((i) => i.tipo == "Documentacion");
      if (this.filtroTipoDocumentacion == 1)
        return this.archivosPersona.filter((i) => i.tipo == "LegajoCredito");
      if (this.filtroTipoDocumentacion == 2)
        return this.archivosPersona.filter((i) => i.tipo == "ComprobantePago");
      if (this.filtroTipoDocumentacion == 3)
        return this.archivosPersona.filter((i) => i.tipo == "Factura");
      if (this.filtroTipoDocumentacion == 4)
        return this.archivosPersona.filter((i) => i.tipo == "Grabaciones");
      if (this.filtroTipoDocumentacion == 5)
        return this.archivosPersona.filter((i) => i.tipo == "Interno");
      if (this.filtroTipoDocumentacion == 6)
        return this.archivosPersona.filter((i) => i.tipo == "SinEspecificar");
      return [];
    },
    archivosPersonaFiltradosBusqueda() {
      if (!this.busquedaArchivo || this.busquedaArchivo.trim() === "") {
        return this.archivosPersonaFiltrados;
      }
      const busqueda = this.busquedaArchivo.toLowerCase();
      return this.archivosPersonaFiltrados.filter((archivo) =>
        archivo.fileName.toLowerCase().includes(busqueda)
      );
    },
  },
  methods: {
    ...mapMutations([
      "alertClear",
      "alertSuccess",
      "alertWarning",
      "alertError",
    ]),
    async prepareCreditosAsociacion() {
      if (this.loadingCreditos) return;

      const personaId = await this.ensurePersonaId();

      if (!personaId) {
        this.creditosPersona = [];
        return;
      }

      await this.loadCreditosPersona();
    },
    async ensurePersonaId() {
      if (this.personaInternaId) {
        return this.personaInternaId;
      }

      if (!this.documento || this.documento.trim() === "") {
        return null;
      }

      try {
        const result = await http.get(
          "personas/listapordocumento?documento=" + this.documento
        );

        if (result && result.count >= 1 && result.list && result.list.length) {
          this.personaInternaId = result.list[0].id;
          return this.personaInternaId;
        }
      } catch (error) {
        this.alertError(error);
      }

      return this.personaInternaId;
    },
    async loadCreditosPersona() {
      if (this.loadingCreditos) {
        return;
      }

      const personaId = await this.ensurePersonaId();

      if (!personaId) {
        this.creditosPersona = [];
        return;
      }

      this.loadingCreditos = true;
      try {
        const result = await http.get(
          "creditos/listporpersona?personaId=" +
            personaId +
            "&IncluirConsultas=false&incluirAnuladosRechazados=false&Limit=50&Page=1"
        );

        let lista;
        if (Array.isArray(result)) {
          lista = result;
        } else if (result && Array.isArray(result.list)) {
          lista = result.list;
        } else if (result && Array.isArray(result.lista)) {
          lista = result.lista;
        } else if (result && Array.isArray(result.List)) {
          lista = result.List;
        } else if (result && Array.isArray(result.items)) {
          lista = result.items;
        } else if (result && Array.isArray(result.data)) {
          lista = result.data;
        } else if (result && result.data && Array.isArray(result.data.list)) {
          lista = result.data.list;
        } else {
          lista = [];
        }

        this.creditosPersona = lista || [];

        if (!this.creditoId && this.creditosPersona.length === 1) {
          const unico = this.creditosPersona[0];
          this.creditoId = unico.creditoId || unico.id || null;
        }

        if (!this.creditoId && this.solicitudId) {
          const asociado = this.creditosPersona.find(
            (credito) =>
              (credito.creditoId || credito.id || "").toString() ===
              this.solicitudId.toString()
          );

          if (asociado) {
            this.creditoId = asociado.creditoId || asociado.id || null;
          }
        }

        if (this.creditoId) {
          const existeCredito = this.creditosPersona.some(
            (credito) => (credito.creditoId || credito.id) === this.creditoId
          );

          if (!existeCredito) {
            this.creditoId = null;
          }
        }
      } catch (error) {
        this.alertError(error);
      } finally {
        this.loadingCreditos = false;
      }
    },
    formatearCredito(credito) {
      if (!credito) return "";

      const partes = [];

      if (credito.numero) partes.push(`Nº ${credito.numero}`);
      if (credito.producto) partes.push(credito.producto);
      if (credito.estado) partes.push(credito.estado);
      else if (credito.subEstadoNombre) partes.push(credito.subEstadoNombre);

      const canal = credito.canalVenta || credito.origen;
      if (canal) partes.push(canal);

      const etiqueta = partes.filter(Boolean).join(" · ");

      if (etiqueta) return `Crédito ${etiqueta}`;

      return `Crédito ${credito.creditoId || credito.id || ""}`.trim();
    },
    async loadEmpresas() {
      this.loading = true;
      try {
        var result = await http.get(
          "empresas/list/porusuario?usuarioId=" + this.user.id
        );
        this.empresas = result.list;
        if (this.empresas.length == 1) {
          // si hay solo 1, no muestro el selector
          this.showSelectorEmpresa = false;
          this.empresaId = this.empresas[0].id;
          this.empresaSubidaId = this.empresas[0].id;
        } else {
          this.empresas.unshift({ id: 0, nombre: "(Todas)" });

          this.showSelectorEmpresa = true;
          this.empresaId = 0;
          if (this.UiCustomUserSettings.empresaPredeterminadaId)
            this.empresaSubidaId =
              this.UiCustomUserSettings.empresaPredeterminadaId;
          else this.empresaSubidaId = this.empresas[1].id;
        }
      } catch (error) {
        // Error
        this.alertError(error);
      }
      this.loading = false;
    },
    async load() {
      this.loading = true;

      try {
        var result = await legajoVirtualService.obtenerArchivosLegajo(
          this.documento,
          this.empresaId
        );

        if (result.success) this.archivosPersona = result.data;
        else this.alertError(result.message);
      } catch (error) {
        this.alertError(error);
      }

      // try {
      //   var result;
      //   if (this.documento)
      //     result = await http.get(
      //       'personas/getfileslegajovirtualpordocumento?documento=' +
      //         this.documento +
      //         '&empresaId=' +
      //         this.empresaId
      //     );
      //   else
      //     result = await http.get(
      //       'personas/getfileslegajovirtualporpersonaid?id=' +
      //         this.personaId +
      //         '&empresaId=' +
      //         this.empresaId
      //     );

      //   for (let i = 0; i < result.length; i++) {
      //     if (result[i].tipo == 'SinEspecificar') {
      //       switch (result[i].extension) {
      //         case '.jpg':
      //         case '.png':
      //         case '.JPG':
      //         case '.PNG':
      //           result[i].icon = 'insert_photo';
      //           break;
      //         case '.pdf':
      //         case '.PDF':
      //           result[i].icon = 'file_copy';
      //           break;
      //         case '.doc':
      //         case '.docx':
      //         case '.DOC':
      //         case '.DOCX':
      //           result[i].icon = 'format_list_bulleted';
      //           break;
      //         default:
      //           result[i].icon = 'attach_file';
      //           break;
      //       }
      //     } else {
      //       if (result[i].tipo == 'Documentacion') {
      //         result[i].icon = 'description';
      //       } else if (result[i].tipo == 'LegajoCredito') {
      //         result[i].icon = 'how_to_reg';
      //       } else if (result[i].tipo == 'Grabaciones') {
      //         result[i].icon = 'mic';
      //       } else if (result[i].tipo == 'ComprobantePago') {
      //         result[i].icon = 'payment';
      //       } else if (result[i].tipo == 'Interno') {
      //         result[i].icon = 'lock';
      //       }
      //     }
      //   }
      //   this.archivosPersona = result;
      // } catch (error) {
      //   // Error
      //   this.alertError(error);
      // }
      this.loading = false;
    },
    async uploadFiles() {
      if (this.archivosASubir.length === 0) {
        this.alertWarning(
          "No se ha seleccionado ningún archivo para subir al legajo."
        );
        return;
      }

      if (this.tipoDocumentacion == "") {
        this.alertWarning(
          "No se ha seleccionado el Tipo de Documentación, por favor, verifique."
        );
        return;
      }

      if (this.tipoDocumentacion === "factura" && !this.creditoId) {
        this.alertWarning(
          "Seleccioná un crédito para asociar la factura antes de continuar."
        );
        return;
      }
      this.loading = true;

      // si se trata de completar documentación, voy a la api de smarter
      if (this.completarDocumentacion) {
        const formData = new FormData();

        formData.append("documento", this.documento);
        formData.append("personaId", "00000000-0000-0000-0000-000000000000");
        formData.append("empresaId", this.empresaSubidaId);
        formData.append("tipoDocumentacion", this.tipoDocumentacion);

        const creditoSeleccionadoId = this.creditoId || this.solicitudId;
        if (creditoSeleccionadoId) {
          formData.append("creditoId", creditoSeleccionadoId);
        }

        //Si se marca completar documentación se cambia el estado del crédito a "Para Analizar"
        if (this.completarDocumentacion) {
          formData.append("completaDocumentacion", this.completarDocumentacion);
          formData.append("comentarios", this.comentarios);
        }

        for (let i = 0; i < this.archivosASubir.length; i++) {
          formData.append("files", this.archivosASubir[i]);
        }

        try {
          await http.post("/personas/uploadfileslegajovirtual", formData);

          this.alertSuccess("Los archivos se subieron con éxito.");

          this.archivosASubir = [];
          this.comentarios = "";
          this.load();
        } catch (error) {
          // Error
          this.alertError(error);
        }
      } else if (this.tipoDocumentacion === "factura") {
        const formData = new FormData();
        const personaId =
          (await this.ensurePersonaId()) ||
          "00000000-0000-0000-0000-000000000000";

        formData.append("documento", this.documento);
        formData.append("personaId", personaId);
        formData.append("empresaId", this.empresaSubidaId);
        formData.append("tipoDocumentacion", this.tipoDocumentacion);
        formData.append("creditoId", this.creditoId);

        for (let i = 0; i < this.archivosASubir.length; i++) {
          formData.append("files", this.archivosASubir[i]);
        }

        try {
          await http.post("/personas/uploadfilesfacturaconsumo", formData);
          this.alertSuccess("Los archivos se subieron con éxito.");

          this.archivosASubir = [];
          this.comentarios = "";
          this.load();
        } catch (error) {
          this.alertError(error);
        }
      } else {
        const response = await legajoVirtualService.uploadLegajoVirtual(
          this.documento,
          null,
          this.creditoId,
          this.empresaSubidaId,
          this.archivosASubir,
          this.completarDocumentacion,
          this.comentarios,
          this.tipoDocumentacion
        );

        if (response.success) {
          this.alertSuccess(response.message);
          this.archivosASubir = [];
          this.comentarios = "";
          this.load();
        } else {
          this.alertError(response.message);
        }
      }

      this.loading = false;
    },
    b64EncodeUnicode(str) {
      return btoa(
        encodeURIComponent(str).replace(
          /%([0-9A-F]{2})/g,
          function (match, p1) {
            return String.fromCharCode("0x" + p1);
          }
        )
      );
    },
    changeType(item) {
      if (this.selectedItem == item) {
        this.cerrarRecategorizacion();
        return;
      }
      this.selectedItem = item;
      this.mostrarDialogRecategorizacion = true;
      console.log(item);
    },
    cerrarRecategorizacion() {
      this.selectedItem = null;
      this.tipoDocumentacion = "";
      this.mostrarDialogRecategorizacion = false;
    },
    obtenerTipoTexto(tipo) {
      const tipos = {
        Documentacion: "Cupo",
        LegajoCredito: "Legajo",
        Grabaciones: "Grabaciones",
        ComprobantePago: "Comprobantes",
        Factura: "Factura",
        Interno: "Interno",
        SinEspecificar: "Otros",
      };
      return tipos[tipo] || tipo;
    },
    async recategorizar() {
      if (this.selectedItem == null) return;

      if (this.tipoDocumentacion == "") {
        this.alertWarning(
          "No se ha seleccionado el Tipo de Documentación, por favor, verifique."
        );
        return;
      }

      const response =
        await legajoVirtualService.updatetipoArchivolegajovirtual(
          this.documento,
          this.selectedItem.fullPath,
          this.tipoDocumentacion
        );

      if (response.success) {
        this.alertSuccess(response.message);
        this.load();
      } else {
        this.alertError(response.message);
      }

      // const formData = new FormData();

      // formData.append('documento', this.documento);
      // formData.append('fileName', this.selectedItem.fullPath);
      // formData.append('tipoDocumentacion', this.tipoDocumentacion);

      // this.loading = true;
      // try {
      //   await http.post('/personas/updatetipoArchivolegajovirtual', formData);

      //   this.alertSuccess('Se registraron los cambios con éxito.');

      //   this.load();
      // } catch (error) {
      //   // Error
      //   this.alertError(error);
      // }
      this.loading = false;
    },
    //Contemplo imágenes y Pdfs
    async verArchivo(file) {
      const response = await legajoVirtualService.obtenerArchivoLegajo(file);

      if (!response.success) {
        this.alertWarning(response.message);
        this.fileImagenUrl = "";
        return;
      }

      if (response.type === "pdf") {
        let pdfWindow = window.open("");
        pdfWindow.document.write(
          `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${response.content}'></iframe>`
        );
      } else if (response.type === "image") {
        this.fileImagenUrl = `data:image/png;base64,${response.content}`; // Ajusta el tipo MIME si es necesario
        this.dialogImagen = true;
      }

      // if (this.isImageOrPdf(file.extension)) {
      //   //convierto el string del path a base64 para poder mandarlo
      //   // uso al función b64EncodeUnicode para poder manejar los caracteres con acentos
      //   let base64String = this.b64EncodeUnicode(file.fullPath); //btoa(file.fullPath);

      //   http
      //     .get('filemanagement/getfilebypath/' + base64String)
      //     .then((resp) => {
      //       if (resp.extension === '.pdf' || resp.extension === '.PDF') {
      //         let pdfWindow = window.open('');
      //         pdfWindow.document.write(
      //           "<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
      //             resp.file +
      //             "'></iframe>"
      //         );
      //       } else {
      //         this.base64ToImage(resp.file);
      //         this.dialogImagen = true;
      //       }
      //     })
      //     .catch((e) => {
      //       this.fileImagenUrl = '';
      //       this.alertError(e);
      //     });
      // } else {
      //   this.alertWarning('Debe seleccionar un archivo con formato de imagen.');
      //   return;
      // }
    },
    async downloadFile(file) {
      const response = await legajoVirtualService.descargarArchivoLegajo(file);
      if (!response.success) {
        this.alertError(response.message);
      } else {
        this.alertSuccess(response.message);
      }

      // //convierto el string del path a base64 para poder mandarlo
      // // uso al función b64EncodeUnicode para poder manejar los caracteres con acentos
      // let base64String = this.b64EncodeUnicode(file.fullPath); //btoa(file.fullPath);

      // http
      //   .get('filemanagement/getfilebypath/' + base64String)
      //   .then((resp) => {
      //     let dataType = '';
      //     switch (resp.extension) {
      //       case '.doc':
      //       case '.docx':
      //       case '.DOC':
      //       case '.DOCX':
      //         dataType = 'data:application/vnd.ms-word';
      //         break;
      //       case '.pdf':
      //       case '.PDF':
      //         dataType = 'data:application/pdf';
      //     }

      //     //necesito hacer esto del arrayBuffer para que despues se pueda hacer el blob
      //     var arrayBuffer = this.base64ToArrayBuffer(resp.file);
      //     this.saveByteArray(resp.fileName, arrayBuffer, dataType);
      //   })
      //   .catch((e) => this.alertError(e));
    },
    async deleteArchivo(file) {
      if (
        confirm("¿Confirma la eliminación del archivo " + file.fullPath + "?")
      ) {
        try {
          const response = await legajoVirtualService.eliminarArchivoLegajo(
            file
          );
          if (response.success) {
            this.alertSuccess(response.message);
            this.load(); // Recargar la lista de archivos
          } else {
            this.alertError(response.message);
          }

          // //convierto el string del path a base64 para poder mandarlo
          // let base64String = this.b64EncodeUnicode(file.fullPath); //btoa(file.fullPath);

          // await http.delete('filemanagement/deletefilebypath/' + base64String);
          // this.alertSuccess('El archivo ha sido eliminado.');
          // this.load();
        } catch (error) {
          // Error
          this.alertError(error);
        }
      }
    },
    base64ToImage(url) {
      const fullUrl = "data:image/png;base64," + url;

      fetch(fullUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "File name");

          this.fileImagenUrl = URL.createObjectURL(file);
        });
    },
    base64ToArrayBuffer(base64) {
      var binaryString = window.atob(base64);
      var binaryLen = binaryString.length;
      var bytes = new Uint8Array(binaryLen);
      for (var i = 0; i < binaryLen; i++) {
        var ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
      }
      return bytes;
    },
    saveByteArray(fileName, byteArrayBuffer, dataType) {
      var blob = new Blob([byteArrayBuffer], { type: dataType });
      var link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    },
    isImageOrPdf(extension) {
      if (
        extension === ".png" ||
        extension === ".jpg" ||
        extension === ".pdf" ||
        extension === ".PNG" ||
        extension === ".JPG" ||
        extension === ".PDF"
      )
        return true;
      return false;
    },
    closeDialog() {
      this.$emit("close-dialog");
    },
    clear() {
      this.archivosASubir = [];
      this.archivosPersona = [];
      this.creditosPersona = [];
      this.creditoId = this.solicitudId || null;
    },
  },
};
</script>

<style scoped>
.legajo-virtual-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.file-list-item {
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
}

.file-list-item:hover {
  background-color: rgba(255, 152, 0, 0.04);
  border-left-color: #fb8c00;
}

.file-table-row {
  transition: all 0.2s ease;
}

.file-table-row:hover {
  background-color: rgba(255, 152, 0, 0.04);
}

.recategorization-panel {
  display: block;
  min-width: 300px;
}

.upload-section {
  min-height: 400px;
  padding: 24px;
  background-color: rgba(255, 152, 0, 0.04);
}

.theme--light .upload-section {
  background: linear-gradient(135deg, #fff8e1 0%, #ffe0b2 100%);
}

.theme--dark .upload-section {
  background-color: rgba(255, 152, 0, 0.08);
}

/* Mejoras en los chips de filtro */
.v-chip-group .v-chip {
  transition: all 0.2s ease;
  font-weight: 500;
}

.v-chip-group .v-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.credit-association-card {
  background-color: transparent;
}

.filter-chip-group::v-deep .v-chip.v-chip--active {
  background-color: #fb8c00 !important;
  color: #ffffff !important;
  border-color: transparent !important;
}

.filter-chip-group::v-deep .v-chip.v-chip--active .v-icon {
  color: #ffffff !important;
}

.filter-chip-group::v-deep .v-chip.v-chip--active .v-chip__content {
  color: #ffffff !important;
}

/* Mejora en los radio buttons */
.v-radio >>> .v-label {
  display: flex;
  align-items: center;
  width: 100%;
}

.v-radio {
  margin-bottom: 4px;
}

/* Animación para expand transition */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

/* File input chips */
.v-file-input >>> .v-chip {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
}

/* Toolbar mejoras */
.v-toolbar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Avatar hover effect */
.v-list-item-avatar {
  transition: transform 0.2s ease;
}

.file-list-item:hover .v-list-item-avatar {
  transform: scale(1.1);
}

/* Botones de acción */
.v-list-item-action .v-btn {
  transition: all 0.2s ease;
}

.v-list-item-action .v-btn:hover {
  transform: scale(1.1);
}

/* Card actions section */
.v-card-actions {
  background-color: transparent;
}

.theme--light .v-card-actions {
  background-color: #fafafa;
}

.theme--dark .v-card-actions {
  background-color: rgba(255, 255, 255, 0.05);
}

/* Tabs styling */
.v-tabs {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Search field */
.v-text-field >>> .v-input__control {
  transition: all 0.3s ease;
}

.v-text-field:hover >>> .v-input__control {
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.2);
}

/* Table improvements */
.v-simple-table {
  border-radius: 8px;
  overflow: hidden;
}

.theme--light .v-simple-table thead th {
  background-color: #fafafa;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .v-simple-table thead th {
  background-color: rgba(255, 255, 255, 0.05);
  font-weight: 600;
}

/* Menu items hover */
.v-list-item:hover {
  background-color: rgba(255, 152, 0, 0.08);
}
</style>
